import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { authAPI } from '../services/api';
import ForgotPassword from './ForgotPassword';
import { useNavigate } from 'react-router-dom';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ identifier: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authAPI.login({ identifier: credentials.identifier, password: credentials.password });
      const { user, token, permissions, expires_at } = response.data;

      if (user.must_reset_password) {
        navigate('/first-time-reset', { state: { user, token, permissions, expires_at } });
        return;
      }

      onLogin({ user, token, permissions, expires_at });
    } catch (err) {
      const data = err.response?.data;
      if (data?.requires_verification) {
        if (data.method === 'email') {
          navigate('/verify-email', { state: { email: data.email, password: credentials.password } });
        } else {
          navigate('/verify-phone', { state: { phone_number: data.phone_number, password: credentials.password } });
        }
      } else {
        setError(data?.message || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    return <ForgotPassword onBackToLogin={() => setShowForgotPassword(false)} />;
  }

  return (
    <Container maxWidth="sm" sx={{ p: { xs: 1, sm: 2 } }}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <Card sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
              <Box component="img" src="/Logo.png" alt="School Sync Logo" sx={{ width: 100, height: 100, objectFit: 'contain', mb: 2 }} />
              <Typography variant="h4" fontWeight={600} color="primary" sx={{ letterSpacing: 0.5 }}>
                School Sync
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Sign in to continue
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                type="text"
                label="Email or Phone Number"
                placeholder="admin@example.com or 0772123456"
                value={credentials.identifier}
                onChange={(e) => setCredentials({...credentials, identifier: e.target.value})}
                required
                margin="normal"
              />
              
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                label="Password"
                value={credentials.password}
                onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                required
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mt: 3, mb: 2 }}
              >
                {loading ? 'SIGNING IN...' : 'SIGN IN'}
              </Button>
              
              <Button
                fullWidth
                variant="text"
                onClick={() => setShowForgotPassword(true)}
                disabled={loading}
              >
                Forgot Password?
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Login;